import { runInAction } from "mobx";

import { preserveCursorPosition } from "@/components/expression/utils/matrix";

import type { DocumentModel } from "@/models/DocumentModel";
import type { MatrixCell } from "@/models/nodes/MatrixNodeModel";
import type { NodeJson } from "@corca/data";

/**
 * Select column relatively to cell position.
 */
export function selectColumn(node: MatrixCell): void {
  const { doc, parent: matrix } = node;
  const { matrixSelection } = doc;
  const { bounds } = matrix;
  const position = matrix.getCellPosition(node);

  if (position) {
    if (matrix.augmentedBarIndices.includes(position.col - 1)) {
      matrixSelection.augmentedBarSelection = [matrix.id, position.col - 1];
      return;
    }

    const from = {
      col: position.col,
      row: 0,
    };
    const to = {
      col: position.col,
      row: bounds.rows - 1,
    };

    if (matrixSelection.setupFromCursor(doc.cursor)) {
      matrixSelection.setSelectionRange(from, to);
    }
  }
}

/**
 * Select row relatively to cell position.
 */
export function selectRow(node: MatrixCell): void {
  const { doc, parent: matrix } = node;
  const { bounds } = matrix;

  const position = matrix.getCellPosition(node);

  if (position) {
    const from = {
      col: 0,
      row: position.row,
    };
    const to = {
      col: bounds.cols - 1,
      row: position.row,
    };

    if (doc.matrixSelection.setupFromCursor(doc.cursor)) {
      doc.matrixSelection.setSelectionRange(from, to);
    }
  }
}

export function preserveSelectionCursorPosition(doc: DocumentModel, cb: () => void): void {
  const { currentSelection } = doc.matrixSelection;

  if (!currentSelection) {
    return;
  }

  const { node, bounds } = currentSelection;
  const deltaX = node.rows === bounds.rows ? -1 : 0;

  preserveCursorPosition(currentSelection.node, cb, deltaX);
}

export function deleteMatrixSelection(doc: DocumentModel): void {
  const { currentSelection } = doc.matrixSelection;

  if (!currentSelection) {
    return;
  }

  doc.transact(() => {
    runInAction(() => {
      const {
        node,
        nodes,
        matrix,
        bounds,
        range: [start],
      } = currentSelection;

      const isEqual = node.rows * node.cols === bounds.rows * bounds.cols;

      if (isEqual) {
        node.setBounds({ rows: 1, cols: 1 });
        node.matrixNodes[0].setChildren([]);
        return;
      }

      if (node.rows === bounds.rows) {
        const row = matrix[0];
        const indices = row.map((_, i) => i + start.col).reverse();

        indices.forEach((i) => {
          node.removeColumn(i);
        });
        return;
      }

      if (node.cols === bounds.cols) {
        const indices = matrix.map((_, i) => i + start.row).reverse();

        indices.forEach((i) => {
          node.removeRow(i);
        });
        return;
      }

      nodes.forEach((cell) => {
        cell.setChildren([]);
      });
    });
  }, "matrix:deleteSelection");

  doc.matrixSelection.resetSelection();
}

export function matrixSelectionJson(doc: DocumentModel): NodeJson | undefined {
  if (!doc.matrixSelection.currentSelection) {
    return;
  }
  const { bounds, nodes } = doc.matrixSelection.currentSelection;
  return {
    type: "group" as const,
    props: {},
    children: [
      {
        type: "matrix" as const,
        props: bounds,
        children: nodes.map((n) => n.toJSON()),
      },
    ],
  };
}
